import {
  GamMediaType,
  type DisplaySize,
  type GamSize,
  type GamSizeMapping,
  type SizeMediaType
} from '@schibsted-nmp/advertising-shared';

import { debug } from '../.debug.js';
import { Api } from '../Api/index.js';

export async function applySizeMapping(
  slot: googletag.Slot,
  sizeMappings: ReadonlyArray<GamSizeMapping>,
  mediaTypes: ReadonlyArray<SizeMediaType>,
  ...abortSignals: ReadonlyArray<AbortSignal>
) {
  if (sizeMappings.length === 0) return;
  const mappedSizes = mapToGamSizes(mediaTypes, sizeMappings);

  await Api(
    (googletag) => {
      const mapping = googletag.sizeMapping();

      // Add each size mapping to the builder
      for (const sizeMap of mappedSizes) {
        mapping.addSize(sizeMap.breakpoint, sizeMap.sizes);
      }

      // Check if [0,0] breakpoint already exists in mappings
      const hasZeroBreakpoint = mappedSizes.some(
        ({ breakpoint: [width, height] }) => width === 0 && height === 0
      );

      // Only add [0,0] fallback if it's not already defined
      if (!hasZeroBreakpoint) {
        // Add a fallback for when no breakpoints match (empty array)
        mapping.addSize([0, 0], []);
      }

      // Apply the size mapping to the slot
      slot.defineSizeMapping(mapping.build());
    },
    ...abortSignals
  );
}

function mapToGamSizes(
  mediaTypes: ReadonlyArray<SizeMediaType>,
  sizeMappings: ReadonlyArray<GamSizeMapping>
) {
  const result: Array<{
    breakpoint: DisplaySize;
    sizes: GamSize[];
  }> = [];
  let fallbackToLegacySizes = false;
  // Extact sizes by name from mediaTypes
  const sizesMappedByName = new Map<string, GamSize>();
  for (const mt of mediaTypes) {
    if (!mt.sizeName) {
      fallbackToLegacySizes = true;
      break;
    }
    if (mt.type === GamMediaType.Native) {
      sizesMappedByName.set(mt.sizeName, 'fluid');
    } else if (mt.width && mt.height) {
      sizesMappedByName.set(mt.sizeName, [mt.width, mt.height]);
    }
  }

  // Sort size mappings by viewport size (largest first)
  const sortedMappings = [...sizeMappings].sort(
    ({ breakpoint: widthA = 0 }, { breakpoint: widthB = 0 }) =>
      Number(widthB) - Number(widthA)
  );

  // Build the result array based on allowed size names
  for (const mapping of sortedMappings) {
    const { allow } = mapping;
    if (!Array.isArray(allow) || allow.length === 0) {
      fallbackToLegacySizes = true;
      break;
    }

    const sizes: GamSize[] = [];
    for (const name of allow) {
      const s = sizesMappedByName.get(name);
      if (s) sizes.push(s);
    }

    if (sizes.length > 0) {
      result.push({ breakpoint: mapping.breakpoint, sizes });
    }
  }
  // if something went wrong, use legacy sizes
  if (fallbackToLegacySizes) {
    debug
      .message(
        'Something went wrong in size mappings, falling back to legacy sizes',
        { mediaTypes, sizeMappings }
      )
      .warn();

    return sizeMappings.map((m) => ({
      breakpoint: m.breakpoint,
      sizes: m.sizes
    }));
  }
  // else we gucci
  return result;
}
